import { request } from "../api";

export interface CompanySettings {
  canonical_api_base_url: string;

  /** Presence enforcement gating + secret rotation. */
  presence_auto_expire_seconds?: number | null;
  presence_enforcement_feature_enabled?: boolean;
  internal_presence_token_configured?: boolean;
  internal_api_allowed_cidrs?: string[];
  internal_presence_token_revealed?: string;

  /** Fleet-wide connectivity and policy-drift reconciliation cadence. */
  heartbeat_interval_seconds?: number;

  /**
   * Company-owned outbound SMTP relay for scheduled reports. Blank
   * `smtp_host` means the process-wide `EMAIL_*` env settings are used
   * instead. `smtp_password` is write-only and never returned here —
   * `smtp_configured` only indicates a configured host, not a stored/valid password.
   */
  smtp_host?: string;
  smtp_port?: number;
  smtp_username?: string;
  smtp_use_tls?: boolean;
  smtp_from_email?: string;
  smtp_configured?: boolean;
}

export interface CompanySettingsPatch {
  canonical_api_base_url?: string;

  presence_auto_expire_seconds?: number | null;
  internal_presence_token?: string;

  heartbeat_interval_seconds?: number;

  smtp_host?: string;
  smtp_port?: number;
  smtp_username?: string;
  /** Omit entirely unless the admin typed a new value — a blank string is a no-op server-side, but omitting is the documented contract. */
  smtp_password?: string;
  smtp_use_tls?: boolean;
  smtp_from_email?: string;
}

export type TestEmailSource = "company_smtp" | "env_fallback";

export interface TestEmailResult {
  sent: boolean;
  recipient: string;
  source: TestEmailSource;
}

export const companiesApi = {
  getSettings: () =>
    request<CompanySettings>(`/api/admin/v1/companies/me/settings/`),

  updateSettings: (patch: CompanySettingsPatch) =>
    request<CompanySettings>(`/api/admin/v1/companies/me/settings/`, {
      method: "PATCH",
      json: patch,
    }),

  /** POST /companies/me/settings/test-email/ — 400 `smtp_not_configured` when neither company SMTP nor the env fallback is usable. */
  testEmail: (recipient: string) =>
    request<TestEmailResult>(`/api/admin/v1/companies/me/settings/test-email/`, {
      method: "POST",
      json: { recipient },
    }),
};
